import { find, findIndex } from "lodash";
import { FormData, FormData_layouts as FormLayout, FormLayoutName } from "types/gql-types";
import { QuestionProps } from "./question";


export const keyFromIdentity =
(identity: string | string[], parent?: string, index?: number): string => {
  const name = typeof identity === "string" ? identity : identity.join("/");

  if (typeof index === "number") {
    return `${parent}.${index}.${name}`;
  }

  return parent ? `${parent}:.${name}` : name;
};

export const formatChildren = (
  children: string[][],
  questionsMap: Record<string, QuestionProps>,
): {key: string, params: QuestionProps}[] =>
  // Skip references to questions the form doesn't define (sc-19450).
  children.flatMap((identity) => {
    const key = keyFromIdentity(identity);
    const params = questionsMap[key];

    return params ? [{ key, params }] : [];
  });

export const getLayout = (formData: FormData): FormData["layouts"][0] => {
  const { layouts } = formData;
  const layout = find(layouts, (layoutItem) => layoutItem.name === FormLayoutName.APPLY);

  if (typeof layout === "undefined") {
    throw new Error("Expected form layout not found.");
  }

  return layout;
};

export const getSectionIndex =
(sectionData: string | undefined, layout: FormLayout): number => {
  if (!sectionData) return 0;
  return findIndex(layout.groups, (section) => section.name === sectionData);
};

export const findMatchingSection = (
  questionIdentities: string[],
  questionSections: Record<string, string>,
  name: string,
): string | undefined => {
  const simpleFind = questionSections[name];
  if (simpleFind) return simpleFind;

  // Longest identity first so `name_first/applicant` wins over a shorter substring like `applicant`.
  const sorted = [...questionIdentities].sort((a, b) => b.length - a.length);
  const matched = sorted.find(
    (identity) =>
      name === identity ||
      name.startsWith(`${identity}/`) ||
      name.startsWith(`${identity}.`) ||
      name.startsWith(`${identity}:`),
  );
  if (!matched) return undefined;
  return questionSections[matched];
};

export const getErrorCount = (errors: any): number => {
  if (!errors) return 0;
  if (typeof errors === "string") return 1;
  if (Array.isArray(errors)) {
    return errors.reduce((acc: number, error: any) => acc + getErrorCount(error), 0);
  }

  return Object.keys(errors).reduce((acc, key) => acc + getErrorCount(errors[key]), 0);
};
